import {PLATFORMS} from './platforms.js';
import {isAppleSilicon} from './apple-silicon.js';

/**
 * Detect the visitor's primary platform from the supplied navigator.
 *
 * Returns one of the values in `PLATFORMS`. Returns `PLATFORMS.UNKNOWN`
 * if no rule matches — consumers should render their "no recommendation"
 * fallback in that case.
 *
 * Detection is purely client-side: User-Agent regex + a small touch-points
 * heuristic (for iPadOS-as-Macintosh) + the WebGL fingerprint for Apple
 * Silicon. UA-CH (`navigator.userAgentData`) preference and CloudFront
 * pre-classification are still pending — see ADR 0007.
 *
 * Order matters: rules are evaluated top-to-bottom and the first match wins.
 * The order below preserves the historical behaviour of the in-component
 * matcher this library replaces.
 *
 * @param {Navigator} [nav] - the navigator to inspect; defaults to the global
 *   `navigator` when running in a browser. Pass an explicit object to test.
 * @returns {string} a `PLATFORMS` value
 */
export function detectPlatform(nav) {
    const navInUse = nav || (typeof navigator !== 'undefined' ? navigator : null);
    if (!navInUse) return PLATFORMS.UNKNOWN;

    const ua = navInUse.userAgent || '';
    const touchPoints = navInUse.maxTouchPoints || 0;

    // iPadOS 13+ reports as Macintosh by default. Distinguish from a real
    // Mac via touch points — Macs have never shipped with a touch screen.
    const isMacintoshUa = /Macintosh/.test(ua);
    const isIPadReportingAsMac = isMacintoshUa && touchPoints > 1;

    // Windows 10 / 11 → Microsoft Store
    if (/Windows NT 10\.0/.test(ua)) return PLATFORMS.MICROSOFT_STORE;

    // Legacy Windows 7 / 8 / 8.1 → direct .msi
    if (/Windows NT 6\.[123]/.test(ua)) return PLATFORMS.WINDOWS_MSI;

    // iPad reporting as Mac → iOS (iPadOS)
    if (isIPadReportingAsMac) return PLATFORMS.IOS;

    // Real Mac on Apple Silicon
    if (isMacintoshUa && isAppleSilicon()) return PLATFORMS.MACOS_ARM;

    // Real Mac on Intel
    if (isMacintoshUa) return PLATFORMS.MACOS_INTEL;

    // iPhone or legacy iPad UA
    if (/iPhone|iPad/.test(ua)) return PLATFORMS.IOS;

    // Huawei devices (no Google Play Services) — must be checked before Android
    if (/HUAWEI|Huawei/.test(ua)) return PLATFORMS.HUAWEI;

    // Android, excluding Huawei (already returned) and ChromeOS (CrOS in UA)
    if (/^(?!.*Huawei|.*HUAWEI|.*CrOS).*Android/.test(ua)) return PLATFORMS.ANDROID;

    return PLATFORMS.UNKNOWN;
}
